import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import type {LinkProps} from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import type {ElementType, ReactNode, Ref} from "react";

import {projectCardClasses as classes} from "./classes.js";

/**
 * The card's thumbnail area. Render it (or omit it entirely) from the host
 * app so the card collapses to its compact layout when there is no thumbnail.
 */
export function ProjectCardThumbnailArea({children}: {children: ReactNode}) {
    return <Box className={classes.thumbnailWrapper}>{children}</Box>;
}

export type ProjectCardTitleProps<C extends ElementType = "a"> = LinkProps<C, {component?: C}> & {
    children: ReactNode;
};

/**
 * The card's linked name. Pass routing props for the host app's link
 * implementation (`href` for a plain anchor, `component={RouterLink} to=...`
 * for the client app).
 */
export function ProjectCardTitle<C extends ElementType = "a">(props: ProjectCardTitleProps<C>) {
    const {children, color = "textPrimary", sx = {display: "inline"}, ...linkProps} = props;
    return (
        <Link color={color} sx={sx} {...linkProps}>
            <Typography color="textPrimary" variant="h6" component="h3" className={classes.name}>
                {children}
            </Typography>
        </Link>
    );
}

export function ProjectCardDescription({children}: {children: ReactNode}) {
    return (
        <Typography component="div" variant="body2" className={classes.description}>
            {children}
        </Typography>
    );
}

export function ProjectCardAuthorRow({children}: {children: ReactNode}) {
    return <Box className={classes.authorRow}>{children}</Box>;
}

export function ProjectCardAuthorSeparator() {
    return (
        <Typography variant="body2" color="textSecondary" className={classes.authorSeparator}>
            &middot;
        </Typography>
    );
}

export interface ProjectCardMetricRowProps {
    children: ReactNode;
    /** Clamp the row to a single line (overflowing metrics hidden). */
    collapsed?: boolean;
    innerRef?: Ref<HTMLDivElement>;
    /** Rendered outside the (clamped) inner row, e.g. a show-more button. */
    trailing?: ReactNode;
}

export function ProjectCardMetricRow({children, collapsed, innerRef, trailing}: ProjectCardMetricRowProps) {
    const innerClassName = collapsed
        ? `${classes.metricRowInner} ${classes.metricRowCollapsed}`
        : classes.metricRowInner;
    return (
        <div className={classes.metricRow}>
            <div className={innerClassName} ref={innerRef}>
                {children}
            </div>
            {trailing}
        </div>
    );
}
